import React, { useContext, useState } from 'react'
import { RequestContext } from '../../../../context/RequestContext';
import { motion } from 'framer-motion';
// Imported under another name: this component has its own closeModal below
import { closeModal as closeModalEntry, swapModal } from '../../../../service/modal-service';
import { FaSearch, FaPenAlt } from 'react-icons/fa';
import { BottomSheet } from '../../../commons/BottomSheet';
import { UserContext } from '../../../../context/UserContext';
import { buildQuickBookCriteria, getUnitsQuestion, isUnitRequired, serviceTypeFor } from '../../../../utils/handyman-quickbook';
import { HandymanJobDetailsStep, MaterialsBadge } from './HandymanJobDetailsStep';
import { ATTENTION_CLASSES } from '../../../commons/BookingFormComponents';

// Every handyman booking starts here: pick a job (or describe one), see its
// details, then the request form opens at date/time as a quick book. There is
// deliberately no path from here to the question wizard, which lacks most
// price-list jobs and their details.
export const HandymanDealItems = (props) => {
    const handymanPriceList = props.handymanPriceList

    const showJobScope = props.showJobScope
    const setRequestForm = props.setRequestForm
    const setIsQuickBook = props.setIsQuickBook;
    const setIsShowDealItems = props.setIsShowDealItems
    // Restrict the picker to one category (e.g. ["Electrical"] on the
    // electrician page) so a specialist page never surfaces unrelated trades.
    const allowedCategories = props.allowedCategories
    // The price list is still being fetched: show placeholders, not the empty state.
    const isLoading = props.isLoading
    // Fallback for deciding whether a job asks for units when the price list
    // has no unitRequired field (see utils/handyman-quickbook.js). The public
    // pages pass it in; the logged-in shell also has it on UserContext.
    const userContext = useContext(UserContext)
    const requestCategorySettings = props.requestCategorySettings?.length
        ? props.requestCategorySettings
        : userContext?.requestCategorySettings

    // Category pills come from the price list itself, in the order the API
    // returns them, so a backend re-categorisation needs no code change here.
    const categoryOptions = [...new Set((handymanPriceList || []).map(item => item.category))]
        .filter(c => !allowedCategories || allowedCategories.includes(c))

    // On a single-category page, lock the filter to that category.
    const lockedCategory = allowedCategories?.length === 1 ? allowedCategories[0] : ""

    const [searchQuery, setSearchQuery] = useState(""); // State to manage search query
    const [selectedCategory, setSelectedCategory] = useState(lockedCategory); // State for selected category
    const [isCustomOpen, setIsCustomOpen] = useState(false)
    const [customText, setCustomText] = useState("")
    // Pulse the description box until it has enough text, instead of
    // autofocus (which opens the phone keyboard over the sheet at once)
    const [isCustomFocused, setIsCustomFocused] = useState(false)
    // The job whose details step is showing (summary, materials, units)
    const [detailsItem, setDetailsItem] = useState(null)

    const { setRequestCriteria, setRequestDescription } = useContext(RequestContext);
    
    // Filter the data based on search query and selected category
    const filteredData = (handymanPriceList || []).filter((item) => {
        const query = searchQuery.toLowerCase();

        // Check if query matches item name or any search term
        const matchesSearch =
        item.itemName.toLowerCase().includes(query) ||
        (item.searchTerms || []).some((term) => term.toLowerCase().includes(query));

        const matchesCategory = selectedCategory
        ? item.category === selectedCategory
        : true;

        // Never show items outside the permitted categories for this page.
        const isAllowed = !allowedCategories || allowedCategories.includes(item.category);

        return matchesSearch && matchesCategory && isAllowed;
    });

    // Free-text request for jobs that are not in the price list.
    // [{1: type}, {2: "Other"}, {21: <user text>}] - the backend has no price
    // for "Other", so it is quoted as a visit and inspection.
    const submitCustomRequest = () => {
        const text = customText.trim()
        if (text.length < 5) return

        const serviceCategory = serviceTypeFor(selectedCategory || lockedCategory || "Electrical")

        const criteria = [
            {1: serviceCategory, criteria: `Handyman service type - ${serviceCategory}`},
            {2: "Other", criteria: "Other"},
            {21: text, criteria: text}
        ]
        confirmBook(criteria)
    }

    const confirmBook = (criteria) => {
        setRequestCriteria(criteria)
        setRequestDescription('Handyman Service')
        setIsQuickBook(true)
        // Hands this sheet's history entry to the form when it has one
        // (opened with openModal); otherwise opens the form with a new entry.
        swapModal(setIsShowDealItems, setRequestForm)
    }

    // Every job goes through the details step first, so the customer sees
    // what is covered and whether they must supply the materials before booking.
    const quickBook = (item, units) => {
        confirmBook(buildQuickBookCriteria(item, units))
    }

    const closeModal = () =>{
        window.scrollTo({ top: 0 });
        closeModalEntry(setIsShowDealItems)
    }
    
  return (
    <BottomSheet
        isOpen={true}
        onClose={closeModal}
        maxWidth="2xl"
    >
        <BottomSheet.Header>
            <BottomSheet.Title>{detailsItem ? 'Service Details' : isCustomOpen ? 'Describe Your Job' : 'Find a Service'}</BottomSheet.Title>
            <BottomSheet.Subtitle>
                {detailsItem
                    ? 'Check what is included, then continue to book'
                    : isCustomOpen
                    ? 'Tell us what you need and we will quote it for you'
                    : 'Pick a service to book instantly'}
            </BottomSheet.Subtitle>
        </BottomSheet.Header>

        {/* Custom request is a separate view, not an expanded footer.
            BottomSheet.Content is the only scrollable, height-aware region;
            the footer budget is ~80px, so a form placed there overflows the
            sheet's maxHeight and gets clipped by its overflow-hidden. */}
        {detailsItem ? (
            <HandymanJobDetailsStep
                item={detailsItem}
                askUnits={isUnitRequired(detailsItem, requestCategorySettings)}
                unitsQuestion={getUnitsQuestion(requestCategorySettings, detailsItem.itemName)?.question}
                onBack={() => setDetailsItem(null)}
                onConfirm={(units) => quickBook(detailsItem, units)}
            />
        ) : isCustomOpen ? (
        <>
        <BottomSheet.Content>
            <div className="space-y-3">
                <label htmlFor="custom-service-request" className="block text-sm font-medium text-gray-700">
                    What do you need done?
                </label>
                <textarea
                    id="custom-service-request"
                    rows={4}
                    maxLength={250}
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    onFocus={() => setIsCustomFocused(true)}
                    onBlur={() => setIsCustomFocused(false)}
                    placeholder="e.g. Kitchen ceiling light flickers and trips the breaker"
                    className={`w-full p-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-orange-200 ${
                        !isCustomFocused && customText.trim().length < 5 ? ATTENTION_CLASSES : ''
                    }`}
                />
                <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>{customText.trim().length < 5 ? 'Please add a little more detail' : 'Looks good'}</span>
                    <span>{customText.length}/250</span>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed bg-gray-50 border border-gray-100 rounded-xl p-3">
                    A technician will review your description and confirm the price before any work begins.
                </p>
            </div>
        </BottomSheet.Content>
        <BottomSheet.Footer>
            <div className="flex gap-3">
                <button
                    type="button"
                    onClick={() => setIsCustomOpen(false)}
                    className="flex-1 px-4 py-3 rounded-xl font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                    Back
                </button>
                <button
                    type="button"
                    onClick={submitCustomRequest}
                    disabled={customText.trim().length < 5}
                    className="flex-1 px-4 py-3 rounded-xl font-semibold text-white bg-linear-to-r from-orange-500 to-amber-500 shadow-lg shadow-orange-200 disabled:opacity-40 disabled:shadow-none transition-opacity"
                >
                    Continue
                </button>
            </div>
        </BottomSheet.Footer>
        </>
        ) : (
        <>
        <BottomSheet.Content>
            <div className="">
                <div className="mb-4 relative">
                  <input type="text"
                    placeholder="Search for a service..."
                    className="w-full pl-11 pr-4 py-3.5 text-sm border border-gray-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-orange-200"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search for a service"
                  />
                  <FaSearch className='absolute text-base top-4 left-4 text-gray-400'/>
                </div>
                {/* Category Filter - pill row is a better thumb target than a native select */}
                {categoryOptions.length > 1 && (
                    <div className='mb-4 flex gap-2 overflow-x-auto scrollbar-hide pb-1'>
                        <button
                            type="button"
                            onClick={() => setSelectedCategory("")}
                            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                                selectedCategory === ""
                                    ? 'bg-orange-50 border-orange-300 text-orange-600'
                                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                        >
                            All
                        </button>
                        {categoryOptions.map((cat) => (
                            <button
                                key={cat}
                                type="button"
                                onClick={() => setSelectedCategory(cat)}
                                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                                    selectedCategory === cat
                                        ? 'bg-orange-50 border-orange-300 text-orange-600'
                                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                                }`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                )}
                {/* Search Results */}
                <div className="pb-2">
                    {isLoading ?
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" aria-busy="true" aria-label="Loading services">
                            {[0, 1, 2, 3, 4].map((i) =>
                                <div key={i} className="flex items-center gap-3 py-3.5 px-4 rounded-2xl border border-gray-100 bg-white animate-pulse">
                                    <div className="flex-1 space-y-2">
                                        <div className="h-3.5 w-3/4 rounded bg-gray-200" />
                                        <div className="h-3 w-1/3 rounded bg-gray-100" />
                                    </div>
                                    <div className="h-9 w-16 rounded-xl bg-gray-200" />
                                </div>
                            )}
                        </div>
                    : filteredData.length > 0 ? 
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {[...filteredData].map((item, index) => 
                            <button
                                key={index}
                                type="button"
                                className="flex items-center gap-3 py-3.5 px-4 rounded-2xl border border-gray-200 bg-white text-left hover:border-orange-200 hover:shadow-md active:scale-[0.98] transition-all duration-200"
                                onClick={() => setDetailsItem(item)}
                                aria-label={`Book ${item.itemName} for $${item.serviceCost.toFixed(2)}`}
                            >
                                <div className='flex-1 min-w-0'>
                                    <div className="text-sm font-semibold text-gray-800 leading-snug">{item.itemName}</div>
                                    <div className="text-xs font-medium text-gray-400 mt-1">
                                        {item.subCategory || item.category}
                                    </div>
                                    {typeof item.includesMaterials === 'boolean' &&
                                        <div className="mt-1.5">
                                            <MaterialsBadge includesMaterials={item.includesMaterials} size="xs" />
                                        </div>
                                    }
                                </div>
                                <span className="shrink-0 bg-linear-to-tr from-orange-600 to-amber-400 px-3 py-2 rounded-xl text-white text-sm font-bold">
                                    ${item.serviceCost.toFixed(2)}
                                </span>
                            </button>
                            )}
                        </div>
                    : 
                        <div className="text-center py-8">
                            <p className="text-gray-500 text-sm">
                                No services found{searchQuery ? ` for "${searchQuery}"` : ''}.
                            </p>
                            <p className="text-gray-400 text-xs mt-1">
                                Describe the job below and we will quote it for you.
                            </p>
                        </div>
                    }
                </div>
            </div>
        </BottomSheet.Content>

        {/* Single-row footer keeps the sheet within its height budget */}
        <BottomSheet.Footer>
            <button
                type="button"
                onClick={() => { setIsCustomOpen(true); setCustomText(searchQuery) }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-orange-600 bg-orange-50 border border-orange-200 hover:bg-orange-100 transition-colors"
            >
                <FaPenAlt className="text-sm" />
                Can&apos;t find your service? Describe it
            </button>
        </BottomSheet.Footer>
        </>
        )}
    </BottomSheet>
  )
}
